import { useOrganization } from '@/contexts/organization-context'

/**
 * Hook to check user's role-based access within an organization
 */
export function useRoleAccess() {
  const { currentOrganization } = useOrganization()

  /**
   * Check if user has access to settings (Manager, Admin, or Owner only)
   */
  const hasSettingsAccess = (): boolean => {
    if (!currentOrganization?.roles || currentOrganization.roles.length === 0) {
      return false
    }
    
    const userRoles = currentOrganization.roles
    const allowedRoles = ['owner', 'admin', 'manager']
    
    return userRoles.some(role => allowedRoles.includes(role.toLowerCase()))
  }

  /**
   * Check if user has admin access (Admin only)
   */
  const hasAdminAccess = (): boolean => {
    if (!currentOrganization?.roles || currentOrganization.roles.length === 0) {
      return false
    }
    
    const userRoles = currentOrganization.roles
    const allowedRoles = ['admin']
    
    return userRoles.some(role => allowedRoles.includes(role.toLowerCase()))
  }

  /**
   * Check if user has access to the Apps SDK Builder (Manager, Admin, or Owner).
   * Must match the Apps SDK Builder backend gate (apps-sdk-builder require_admin,
   * which allows the same role set). Note this is intentionally WIDER than
   * hasAdminAccess() — Guardian and other admin-only tools must keep using
   * hasAdminAccess(), not this.
   */
  const hasAppsSdkAccess = (): boolean => {
    if (!currentOrganization?.roles || currentOrganization.roles.length === 0) {
      return false
    }

    const userRoles = currentOrganization.roles
    const allowedRoles = ['admin', 'manager', 'owner']

    return userRoles.some(role => allowedRoles.includes(role.toLowerCase()))
  }

  /**
   * Check if user is organization owner
   */
  const isOwner = (): boolean => {
    if (!currentOrganization?.roles || currentOrganization.roles.length === 0) {
      return false
    }
    
    const userRoles = currentOrganization.roles
    return userRoles.some(role => role.toLowerCase() === 'owner')
  }

  /**
   * Check if user has specific role
   */
  const hasRole = (requiredRole: string): boolean => {
    if (!currentOrganization?.roles || currentOrganization.roles.length === 0) {
      return false
    }
    
    const userRoles = currentOrganization.roles
    return userRoles.some(role => role.toLowerCase() === requiredRole.toLowerCase())
  }

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (requiredRoles: string[]): boolean => {
    if (!currentOrganization?.roles || currentOrganization.roles.length === 0) {
      return false
    }
    
    const userRoles = currentOrganization.roles.map(role => role.toLowerCase())
    const normalizedRequiredRoles = requiredRoles.map(role => role.toLowerCase())
    
    return userRoles.some(role => normalizedRequiredRoles.includes(role))
  }

  /**
   * Get user's primary role (first role in the array)
   */
  const getPrimaryRole = (): string | null => {
    if (!currentOrganization?.roles || currentOrganization.roles.length === 0) {
      return null
    }
    
    return currentOrganization.roles[0]
  }

  /**
   * Get all user roles
   */
  const getAllRoles = (): string[] => {
    if (!currentOrganization?.roles || currentOrganization.roles.length === 0) {
      return []
    }
    
    return currentOrganization.roles
  }

  return {
    hasSettingsAccess,
    hasAdminAccess,
    hasAppsSdkAccess,
    isOwner,
    hasRole,
    hasAnyRole,
    getPrimaryRole,
    getAllRoles,
    currentOrganization
  }
}